import React, { ReactNode } from "react";
import {
  KeyValuePairs,
  KeyValuePairsProps,
  Popover,
  Spinner,
} from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import {
  getAcidLink,
  getFlashLink,
  getFusenWfLink,
  getTransactionWfLink,
} from "src/commons/external-link-helper";
import { NUMBER_CELL_WRAPPER } from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import { Domain } from "src/config/BartLambdaConfig";
import Decimal from "decimal.js";

interface LatestAdjustmentModalProps {
  fixedFeeConfig?: FixedFeeConfig;
  loading: boolean;
  child: ReactNode;
  domain: Domain;
}

const getTransactionItems = (
  externalId: string,
  uslIdempotenceIdPrefix: string,
  domain: Domain,
  isTest: boolean,
) => {
  return [
    {
      label: "Transaction",
      value: (
        <a
          href={`/transactionsearch/external_id/${externalId}`}
          target="_blank"
        >
          {externalId}
        </a>
      ),
    },
    {
      label: "FuseBillingService Workflows",
      value: (
        <span>
          <a href={getTransactionWfLink(externalId, domain)} target="_blank">
            Transaction WF
          </a>
          ,&nbsp;
          <a href={getFusenWfLink(externalId, domain)} target="_blank">
            FUSEN WF
          </a>
        </span>
      ),
    },
    {
      label: "Other Links",
      value: (
        <span>
          <a href={getAcidLink(externalId, domain)} target="_blank">
            ACID
          </a>
          ,&nbsp;
          <a
            href={getFlashLink(
              `${uslIdempotenceIdPrefix}.${externalId.substring(externalId.lastIndexOf(".") + 1)}.${externalId}`,
              domain,
              isTest,
            )}
            target="_blank"
          >
            FLASH
          </a>
        </span>
      ),
    },
  ];
};

const getMostRecentAdjustment = (
  fixedFeeConfig: FixedFeeConfig,
  mostRecentOccurrenceDate: string,
  mostRecentOccurrenceIndex: number,
  domain: Domain,
) => {
  const mostRecentOccurrenceResult =
    fixedFeeConfig.adjustments![mostRecentOccurrenceDate];

  const mostRecentAdjustmentItems: { label: ReactNode; value: ReactNode }[] = [
    {
      label: "Date",
      value: mostRecentOccurrenceDate,
    },
    {
      label: "Adjusting Period",
      value:
        mostRecentOccurrenceResult.periodStartDate +
        " to " +
        mostRecentOccurrenceResult.periodEndDate,
    },
    {
      label: "Queried Activations/Renewals",
      value:
        mostRecentOccurrenceResult.activeBenefitCount?.toLocaleString("en-US"),
    },
    {
      label: "Queried Deferral Revenue",
      value: NUMBER_CELL_WRAPPER(mostRecentOccurrenceResult.deferredRevenue),
    },
    {
      label: "Revised Plan Price",
      value: NUMBER_CELL_WRAPPER(mostRecentOccurrenceResult.revisedPlanPrice),
    },
  ];

  const adjustmentAmount = Number(
    mostRecentOccurrenceResult.adjustmentAmount!.string,
  );
  if (adjustmentAmount === 0) {
    mostRecentAdjustmentItems.push({
      label: "Amount",
      value: "0",
    });
  } else {
    const isTrueUp = adjustmentAmount > 0;
    mostRecentAdjustmentItems.push({
      label: "Amount",
      value: isTrueUp ? (
        <span style={{ color: "#00802f" }}>
          {adjustmentAmount.toLocaleString("en-US")} (True-Up)
        </span>
      ) : (
        <span style={{ color: "#db0000" }}>
          {(-adjustmentAmount).toLocaleString("en-US")} (True-Down)
        </span>
      ),
    });

    const externalId = `${fixedFeeConfig.fixedFeeId}-${mostRecentOccurrenceIndex}.${isTrueUp ? "TrueUp" : "TrueDown"}`;

    mostRecentAdjustmentItems.push(
      ...getTransactionItems(
        externalId,
        "Axon.Fuse.Transaction",
        domain,
        fixedFeeConfig.environment === "TEST",
      ),
    );
  }
  return mostRecentAdjustmentItems;
};

const getMostRecentSubBalanceShift = (
  fixedFeeConfig: FixedFeeConfig,
  mostRecentOccurrenceDate: string,
  mostRecentOccurrenceIndex: number,
  domain: Domain,
) => {
  const mostRecentOccurrenceResult =
    fixedFeeConfig.adjustments![mostRecentOccurrenceDate];

  let longTermReceivables = new Decimal(
    fixedFeeConfig.reclassBalance!.longTermReceivables!.string,
  );
  let longTermDeferredRevenue = new Decimal(
    fixedFeeConfig.reclassBalance!.longTermDeferredRevenue!.string,
  );
  const contractPrice = new Decimal(fixedFeeConfig.contractPrice!.string);

  Object.values(fixedFeeConfig.adjustments!).forEach((occurrenceResult) => {
    if (occurrenceResult.receivableShiftAmount) {
      longTermReceivables = longTermReceivables.sub(
        new Decimal(occurrenceResult.receivableShiftAmount!.string),
      );
    }
    if (occurrenceResult.deferralRevenueShiftAmount) {
      longTermDeferredRevenue = longTermDeferredRevenue.sub(
        new Decimal(occurrenceResult.deferralRevenueShiftAmount!.string),
      );
    }
  });

  const mostRecentSubBalanceShiftItems: {
    label: ReactNode;
    value: ReactNode;
  }[] = [
    {
      label: "Long-term AR (After)",
      value: NUMBER_CELL_WRAPPER(longTermReceivables),
    },
    {
      label: "Long-term Deferred Revenue (After)",
      value: NUMBER_CELL_WRAPPER(longTermDeferredRevenue),
    },
    {
      label: "Short-term AR (After)",
      value: NUMBER_CELL_WRAPPER(contractPrice.sub(longTermReceivables)),
    },
    {
      label: "Short-term Deferred-Revenue (After)",
      value: NUMBER_CELL_WRAPPER(contractPrice.sub(longTermDeferredRevenue)),
    },
  ];

  const receivableShiftAmount = Number(
    mostRecentOccurrenceResult.receivableShiftAmount!.string,
  );
  const deferredRevenueShiftAmount = Number(
    mostRecentOccurrenceResult.deferralRevenueShiftAmount!.string,
  );
  if (receivableShiftAmount === 0) {
    mostRecentSubBalanceShiftItems.push({
      label: "Receivable Shift Amount",
      value: "0",
    });
  } else {
    mostRecentSubBalanceShiftItems.push({
      label: "Receivable Shift Amount",
      value:
        receivableShiftAmount > 0 ? (
          <span style={{ color: "#00802f" }}>
            {receivableShiftAmount.toLocaleString("en-US")}
          </span>
        ) : (
          <span style={{ color: "#db0000" }}>
            {(-receivableShiftAmount).toLocaleString("en-US")}
          </span>
        ),
    });
  }

  if (deferredRevenueShiftAmount === 0) {
    mostRecentSubBalanceShiftItems.push({
      label: "Deferred Revenue Shift Amount",
      value: "0",
    });
  } else {
    mostRecentSubBalanceShiftItems.push({
      label: "Deferred Revenue Shift Amount",
      value:
        deferredRevenueShiftAmount > 0 ? (
          <span style={{ color: "#00802f" }}>
            {deferredRevenueShiftAmount.toLocaleString("en-US")}
          </span>
        ) : (
          <span style={{ color: "#db0000" }}>
            {(-deferredRevenueShiftAmount).toLocaleString("en-US")}
          </span>
        ),
    });
  }

  if (receivableShiftAmount != 0 || deferredRevenueShiftAmount != 0) {
    const externalId = `${fixedFeeConfig.fixedFeeId}-${mostRecentOccurrenceIndex}.SubBalShift`;

    mostRecentSubBalanceShiftItems.push(
      ...getTransactionItems(
        externalId,
        "Axon.Fuse.Charge",
        domain,
        fixedFeeConfig.environment === "TEST",
      ),
    );
  }

  return mostRecentSubBalanceShiftItems;
};

const AdditionalInfoPopover: React.FC<LatestAdjustmentModalProps> = ({
  fixedFeeConfig,
  loading,
  child,
  domain,
}) => {
  let content;
  if (loading) {
    content = <Spinner size="large" />;
  } else if (!fixedFeeConfig) {
    content = <div>Something went wrong, please try again.</div>;
  } else if (
    !fixedFeeConfig.adjustments ||
    Object.keys(fixedFeeConfig.adjustments).length === 0
  ) {
    content = <div>There are no adjustment records.</div>;
  } else {
    const occurrenceResults = fixedFeeConfig.adjustments;
    const occurrenceDates = Object.keys(occurrenceResults);
    const executedOccurrenceDates = occurrenceDates.filter((occurrenceDate) => {
      return occurrenceResults[occurrenceDate].adjustmentAmount !== undefined;
    });

    const adjustmentSummary: KeyValuePairsProps.Pair = {
      type: "pair",
      label: "Executed Occurrences (to date)",
      value: `${executedOccurrenceDates.length} out of ${occurrenceDates.length}`,
    };

    if (executedOccurrenceDates.length === 0) {
      content = <KeyValuePairs columns={1} items={[adjustmentSummary]} />;
    } else {
      const mostRecentOccurrenceDate = executedOccurrenceDates.reduce((a, b) =>
        a > b ? a : b,
      );

      const mostRecentAdjustment: KeyValuePairsProps.Group = {
        type: "group",
        title: "Most Recent Adjustment",
        items: [
          {
            label: adjustmentSummary.label,
            value: adjustmentSummary.value,
          },
          ...getMostRecentAdjustment(
            fixedFeeConfig,
            mostRecentOccurrenceDate,
            executedOccurrenceDates.length,
            domain,
          ),
        ],
      };
      content = [mostRecentAdjustment];

      if (
        occurrenceResults[mostRecentOccurrenceDate].deferralRevenueShiftAmount
      ) {
        const mostRecentSubBalanceShift: KeyValuePairsProps.Group = {
          type: "group",
          title: "Most Recent SubBalanceShift",
          items: getMostRecentSubBalanceShift(
            fixedFeeConfig,
            mostRecentOccurrenceDate,
            executedOccurrenceDates.length,
            domain,
          ),
        };
        content.push(mostRecentSubBalanceShift);
      }

      content = <KeyValuePairs columns={content.length} items={content} />;
    }
  }

  return (
    <Popover
      fixedWidth
      header="Most Recent Adjustment/SubBalanceShift Info"
      size="large"
      triggerType="custom"
      content={content}
    >
      {child}
    </Popover>
  );
};

export default AdditionalInfoPopover;
